<template>
    <div class="bg-white border-b-px border-interactive-9 hover:drop-shadow-[0_2px_12px_rgba(0,0,0,0.12)] hover:z-[9] xxs:mb-4 lg:mb-0"
         :class="{'drop-shadow-[0_2px_12px_rgba(0,0,0,0.12)] z-10': isGroupOpen || isMobile}">
        <div v-if="!isGroupOpen && !isMobile" class="flex flex-row items-center h-12 cursor-pointer">
            <div class="flex flex-1 items-center" @click="toggleOpenGroup">
                <div class="xxs:w-full md:w-auto md:flex-1 flex items-center">
                    <div class="w-10 h-8 flex items-center justify-center">
                        <img src="../../assets/icons/chevron-down.svg" alt="open"/>
                    </div>
                    <Avatar :firstName="group.name" :avatar="group.avatar" shape="square" />
                    <p class="text-body-6 text-default-1 font-medium ml-2">{{ group.name }}</p>
                </div>
                <div class="flex h-6 z-0 relative w-1/5">
                    <StackedMembersAvatar
                        :members="firstThreeMembers"
                        :totalMembers="group.members_count"
                    />
                </div>
                <p class="text-body-6 font-normal text-default-1 w-1/4">{{ useFormatDate(group.created_at) }}</p>
            </div>
            <div v-if="isAdmin" class="w-8 h-8 flex items-center justify-start" @click="openGroupAndOptionsDropdown">
                <img src="../../assets/icons/dots-gray.svg" alt=""/>
            </div>
        </div>
        <div v-if="isGroupOpen || isMobile" class="flex xxs:flex-col lg:flex-row items-start p-5 w-full">
            <img src="../../assets/icons/chevron-up.svg" class="xxs:hidden lg:block mr-0.5 mt-0 lg:mt-6 cursor-pointer" alt="open" @click="toggleOpenGroup"/>
            <Avatar :firstName="group.name" :avatar="group.avatar" size="large" shape="square" class="lg:ml-4 shrink-0" />
            <div class="flex flex-col xxs:w-full lg:w-1/4 lg:ml-4 xxs:mt-4 lg:mt-0">
                <p class="text-body-4 font-medium text-default-1 mb-1 break-words">{{ group.name }}</p>
                <p class="xxs:text-body-6 lg:text-body-7 font-medium text-default-3">Simple group</p>
                <p class="xxs:text-body-5 lg:text-body-6 text-default-1 font-normal">{{ useFormatDate(group.created_at) }}</p>
            </div>
            <div class="flex-flex-col w-3/4 lg:pl-4 box-border xxs:mt-4 lg:mt-0">
                <p class="xxs:text-body-6 lg:text-body-7 font-medium text-default-2">Description</p>
                <p class="xxs:text-body-5 lg:text-body-6 font-normal text-default-1">{{ group.description }}</p>

                <div class="flex flex-row mt-8 w-full">
                    <div class="flex flex-col w-1/2" v-if="hasMembers">
                        <p class="text-body-7 text-default-2 font-medium mb-1">Members</p>
                        <div class="flex xxs:flex-col lg:flex-row z-0 relative lg:items-center">
                            <StackedMembersAvatar
                                :members="firstThreeMembers"
                                :totalMembers="group.members_count"
                            />
                            <p v-if="group.members_count > 0"
                               class="xxs:text-body-6 lg:text-body-7 text-default-2 font-medium xxs:mt-4 lg:mt-0 lg:ml-2.5 cursor-pointer"
                               @click="router.push({ name: 'members', query: { group: group.name } })">
                                View all ({{ group.members_count }})
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <Menu v-if="isAdmin" as="div" class="inline-block text-left xxs:absolute lg:relative xxs:top-4 xxs:right-6 lg:top-auto lg:right-auto" @close="isGroupOptionsVisible = false">
                <div>
                    <MenuButton
                            @click="isGroupOptionsVisible = !isGroupOptionsVisible"
                            class="flex justify-end items-center w-8 h-8"
                            ref="optionsDropdownButton"
                    >
                        <img src="../../assets/icons/dots-gray.svg" alt=""/>
                    </MenuButton>
                </div>

                <transition
                        enter-active-class="transition duration-100 ease-out"
                        enter-from-class="transform scale-95 opacity-0"
                        enter-to-class="transform scale-100 opacity-100"
                        leave-active-class="transition duration-75 ease-in"
                        leave-from-class="transform scale-100 opacity-100"
                        leave-to-class="transform scale-95 opacity-0"
                >
                    <div v-if="isGroupOptionsVisible">
                        <MenuItems

                                class="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                        >
                            <div class="px-1 py-1">
                                <MenuItem v-slot="{ active }" v-for="option in optionsButton" :key="option.event">
                                    <button
                                            :class="[{
                                            'bg-background-3': active,
                                            'text-gray-900': !active,
                                            'group flex justify-start w-full items-center rounded-md px-2 py-2 text-sm': true
                                        },
                                        option.event === 'delete-group' ? 'text-interactive-7': '']"
                                            @click="option.action"
                                    >
                                        <img class="w-4 mr-2.5" :src="option.icon" alt=""/>
                                        {{ option.name }}
                                    </button>
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </div>
                </transition>
            </Menu>
        </div>
        <Modal :show="removeMembersFromGroupModalVisibility" @close="closeRemoveMembersFromGroupModal" :overflowHidden="false">
            <RemoveGroupMembers :group="group" v-if="removeMembersFromGroupModalVisibility" @removeMembers="removeMembers" />
        </Modal>
    </div>
</template>
<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import emitter from "@/plugins/mitt";
import StackedMembersAvatar from "@/components/sections/groups/StackedMembersAvatar.vue";
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/vue";
import Avatar from "@/components/custom/Avatar.vue";
import {useFormatDate} from "@/composables/useFormatDate";
import { useRouter } from 'vue-router';
import {useIsMobile} from "@/composables/useIsMobile";
import RemoveGroupMembers from "@/components/modals/partials/RemoveGroupMembers.vue";
import Modal from "@/components/core/Modal.vue";
import {toast} from "vue3-toastify";
import {useGroups} from "@/composables/useGroups";
import {useAuth} from "@/stores/AuthStore";
import {useIncludeExcludeOptions} from "@/composables/useIncludeExcludeOptions";

const {resetIncludeExclude} = useIncludeExcludeOptions();

const {organization, isAdmin} = useAuth();
const {isMobile} = useIsMobile();
const {disassociateMembers} = useGroups();
const router = useRouter();

const props = defineProps({
    group: {
        type: Object,
        required: true
    }
})

const emit = defineEmits(['close-all-groups', 'add-member', 'delete-group', 'create-chat', 'membersRemoved', 'delete-chat-group']);

const removeMembersFromGroupModalVisibility = ref(false);
const isGroupOpen = ref(false);
const isGroupOptionsVisible = ref(false);

const optionsButton = [
    {
        name: 'Update group',
        icon: require('../../assets/icons/settings.svg'),
        action: () => {
            router.push({name: 'edit-group', params: {uid: props.group.uid}});
        }
    },
    {
        name: 'Add member',
        icon: require('../../assets/icons/add-member.svg'),
        action: () => {
            isGroupOptionsVisible.value = false;
            emit('add-member', props.group.uid);
        },
    },
    {
        name: 'Remove member',
        icon: require('../../assets/icons/blocked-members.svg'),
        action: () => {
            isGroupOptionsVisible.value = false;
            removeMembersFromGroupModalVisibility.value = true;
        },
    },
    {
        name: 'Delete group',
        icon: require('../../assets/icons/trash-dark.svg'),
        action: () => deleteGroup(props.group),
    },
]
const optionsDropdownButton = ref(null)

const hasMembers = computed(() => {
    return props.group.members_count > 0;
});

const firstThreeMembers = computed(() => {
    return props.group.members.slice(0, 3);
});

function toggleOpenGroup() {
    isGroupOptionsVisible.value = false;
    isGroupOpen.value = !isGroupOpen.value;
    if (isGroupOpen.value) {
        emitter.emit('close-all-groups', props.group.uid);
    }
}

function closeRemoveMembersFromGroupModal() {
    removeMembersFromGroupModalVisibility.value = false

    resetIncludeExclude();
}

function openGroupAndOptionsDropdown() {
    isGroupOpen.value = true

    setTimeout(() => {
        optionsDropdownButton.value.$el.click();
        isGroupOptionsVisible.value = true;
    }, 50);

    if (isGroupOpen.value) {
        emitter.emit('close-all-groups', props.group.uid);
    }
}

function deleteGroup(group) {
    isGroupOptionsVisible.value = false;
    emit('delete-group', group);
}

function removeMembers(selectedMembers) {
    removeMembersFromGroupModalVisibility.value = false;

    if(selectedMembers.include.length === 0) {
        return;
    }

    disassociateMembers(organization.slug, props.group.uid, {members: selectedMembers})
        .then(() => {
            if(selectedMembers.include.length === 1 && !selectedMembers.include.includes('*')) {
                toast.success('Member removed successfully');
            } else if(selectedMembers.length > 1) {
                toast.success('Members removed successfully');
            }
        })
        .catch(() => {
            toast.error('There was an error managing the group members. Please try again later');
        }).finally(() => {
            resetIncludeExclude();
            emit('membersRemoved');
        })
}

onMounted(() => {
    emitter.on('close-all-groups', (exceptUid) => {
        if (exceptUid !== props.group.uid) {
            isGroupOpen.value = false;
        }
    })
})

onBeforeUnmount(() => {
    emitter.all.clear();
})
</script>
